import { useEffect, useRef, useState } from "react";
import api, { BACKEND_URL } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Image as ImageIcon } from "lucide-react";

export default function Settings() {
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  if (!form) return <div>{t("loading")}</div>;

  const save = async () => {
    try {
      await api.put("/settings", form);
      toast.success(t("saved"));
    } catch {
      toast.error("Error");
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success(t("logoUploaded"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const logoFullUrl = form.logo_url ? (form.logo_url.startsWith("http") ? form.logo_url : `${BACKEND_URL}${form.logo_url}`) : "";

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="uppercase-label">{t("settings")}</div>
        <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">{t("settings")}</h1>
        <div className="mt-3 divider-accent" />
      </div>

      <div className="dashboard-card max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label={t("companyName")}><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" /></Field>
          <Field label={t("phone")}><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label={t("email")}><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label={t("website")}><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
          <Field label={t("crNumber")}><Input value={form.cr_number || ""} onChange={(e) => setForm({ ...form, cr_number: e.target.value })} data-testid="settings-cr" /></Field>
          <Field label={t("licenseNumber")}><Input value={form.license_number || ""} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="settings-license" /></Field>
          <div className="sm:col-span-2">
            <Field label={t("licenseCategory")}><Input value={form.license_category || ""} onChange={(e) => setForm({ ...form, license_category: e.target.value })} data-testid="settings-license-category" /></Field>
          </div>
          <div className="sm:col-span-2">
            <Field label={t("taxNumber")}><Input value={form.tax_number || ""} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} data-testid="settings-tax" /></Field>
          </div>
          <div className="sm:col-span-2">
            <Field label={t("address")}><Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          </div>
          <Field label={t("primaryColor")}>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.primary_color || "#0A192F"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-11 w-16 p-1" />
              <Input value={form.primary_color || ""} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
            </div>
          </Field>
          <Field label={t("accentColor")}>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.accent_color || "#D4AF37"} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-11 w-16 p-1" />
              <Input value={form.accent_color || ""} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} />
            </div>
          </Field>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase-label">{t("uploadLogo")}</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] grid place-items-center overflow-hidden">
                {logoFullUrl ? (
                  <img src={logoFullUrl} alt="logo" className="object-contain w-full h-full" data-testid="settings-logo-preview" />
                ) : (
                  <ImageIcon className="text-[hsl(var(--muted-foreground))]" size={26} />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn">
                  <Upload size={15} className="me-2" />
                  {uploading ? t("loading") : t("uploadLogo")}
                </Button>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">PNG · JPG · WEBP · max 5MB</div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <Button onClick={save} data-testid="settings-save-btn" className="bg-[#0A192F] hover:bg-[#112240] text-white">
            {t("save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);
